'use client'

import React, { useState } from 'react'
import { getSiteCheckT, formatFinding, type SiteCheckPack } from '@/lib/website-security-check-translations'
import WidgetFooter from './WidgetFooter'
import { SUDOKUFLAGS_LINK, SUDOKUFLAGS_BRAND, sudokuflagsCopy } from '@/lib/sudokuflags-promo'

type Props = {
    h1: string
    lang: string
    toolId?: string
    toolSlug?: string
    h1En?: string
    translations?: { suggest: string; downloadWidget: string; getWidget: string }
}

type Finding = { status: 'ok' | 'warn' | 'bad' | 'info'; code: string; params?: Record<string, string | number> }
type Report = {
    reachable: boolean
    final_url: string | null
    checked_at: string
    tone: 'ok' | 'warn' | 'bad'
    findings: Finding[]
}

const DOT: Record<string, string> = {
    ok: 'bg-green-500', warn: 'bg-amber-500', bad: 'bg-red-500', info: 'bg-blue-400',
}
const PILL: Record<string, string> = {
    ok: 'text-green-700 bg-green-100', warn: 'text-amber-700 bg-amber-100',
    bad: 'text-red-700 bg-red-100', info: 'text-blue-700 bg-blue-100',
}
const HEAD: Record<string, string> = {
    ok: 'bg-green-500', warn: 'bg-amber-500', bad: 'bg-red-500',
}
const HEAD_ICON: Record<string, string> = { ok: '✓', warn: '!', bad: '×' }

// Footer link copy, specific to this widget (it is a widget/tool, not a
// "calculator"). Localized for all 8 site locales, English fallback.
const FOOTER: Record<string, { suggest: string; downloadWidget: string; getWidget: string }> = {
    en: { suggest: 'Suggest an improvement or request a new widget.', downloadWidget: 'Download Widget', getWidget: 'Get a Free Widget for this tool' },
    ru: { suggest: 'Предложить улучшение или запросить новый виджет.', downloadWidget: 'Скачать виджет', getWidget: 'Получить бесплатный виджет для этого инструмента' },
    de: { suggest: 'Eine Verbesserung vorschlagen oder ein neues Widget anfragen.', downloadWidget: 'Widget herunterladen', getWidget: 'Kostenloses Widget für dieses Tool' },
    es: { suggest: 'Sugerir una mejora o solicitar un nuevo widget.', downloadWidget: 'Descargar widget', getWidget: 'Obtener un widget gratis para esta herramienta' },
    fr: { suggest: 'Suggérer une amélioration ou demander un nouveau widget.', downloadWidget: 'Télécharger le widget', getWidget: 'Obtenir un widget gratuit pour cet outil' },
    it: { suggest: 'Suggerisci un miglioramento o richiedi un nuovo widget.', downloadWidget: 'Scarica il widget', getWidget: 'Ottieni un widget gratuito per questo strumento' },
    pl: { suggest: 'Zaproponuj ulepszenie lub poproś o nowy widget.', downloadWidget: 'Pobierz widget', getWidget: 'Pobierz darmowy widget dla tego narzędzia' },
    lv: { suggest: 'Ieteikt uzlabojumu vai pieprasīt jaunu logrīku.', downloadWidget: 'Lejupielādēt logrīku', getWidget: 'Iegūt bezmaksas logrīku šim rīkam' },
}

function esc(s: string): string {
    return String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
}

export default function SiteCheckWidget({ h1, lang, toolId, toolSlug, h1En }: Props) {
    const t: SiteCheckPack = getSiteCheckT(lang)
    const [url, setUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const [report, setReport] = useState<Report | null>(null)
    const [error, setError] = useState<string | null>(null)

    async function run(e: React.FormEvent) {
        e.preventDefault()
        let u = url.trim()
        if (!u) { setError(t.ui.errMissing); setReport(null); return }
        // Accept a bare domain ("example.com") by assuming https:// — the field
        // is plain text so the browser never blocks it with "enter a URL".
        if (!/^https?:\/\//i.test(u)) u = 'https://' + u
        if (u !== url) setUrl(u)
        setLoading(true); setError(null); setReport(null)
        try {
            const res = await fetch('/api/sitecheck?url=' + encodeURIComponent(u))
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                setError(mapError(t, res.status, body && body.error))
                return
            }
            const data: Report = await res.json()
            setReport(data)
        } catch {
            setError(t.ui.errNetwork)
        } finally {
            setLoading(false)
        }
    }

    function hostOf(r: Report): string {
        try { return new URL(r.final_url || url).hostname } catch { return url }
    }

    function downloadPdf(r: Report) {
        const host = hostOf(r)
        const when = r.checked_at ? new Date(r.checked_at).toLocaleString() : new Date().toLocaleString()
        const rows = r.findings.map((f) => {
            const ft = formatFinding(t, f.code, f.params)
            const color = f.status === 'ok' ? '#1a7f4b' : f.status === 'warn' ? '#9a6b00' : f.status === 'bad' ? '#b3261e' : '#2f6fe0'
            return `<tr>
                <td style="vertical-align:top;padding:9px 10px 9px 0;white-space:nowrap">
                  <span style="display:inline-block;font-size:11px;font-weight:700;color:#fff;background:${color};border-radius:4px;padding:2px 7px">${esc(t.ui.labels[f.status])}</span>
                </td>
                <td style="padding:9px 0;border-bottom:1px solid #eee">
                  <div style="font-weight:600;color:#111">${esc(ft.title)}</div>
                  <div style="color:#444;font-size:13px;margin-top:2px">${esc(ft.detail)}</div>
                </td></tr>`
        }).join('')
        const headColor = r.tone === 'ok' ? '#1a7f4b' : r.tone === 'warn' ? '#9a6b00' : '#b3261e'
        const origin = typeof window !== 'undefined' ? window.location.origin : ''
        const promo = sudokuflagsCopy(lang)
        const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(h1)} - ${esc(host)}</title>
          <style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact}
          body{font:14px/1.6 system-ui,Segoe UI,Roboto,Arial,sans-serif;color:#111;margin:32px;max-width:720px}
          .head{display:flex;align-items:center;gap:14px;margin-bottom:18px;padding-bottom:14px;border-bottom:2px solid #eef2f7}
          .head .logo{height:38px;width:auto;flex:0 0 auto}
          .head h1{font-size:20px;margin:0}
          .head .meta{color:#555;font-size:13px;margin-top:3px}
          .head .gen{color:#8a8f98;font-size:11px;margin-top:2px}
          .verdict{display:inline-block;color:#fff;background:${headColor};font-weight:700;padding:6px 14px;border-radius:999px;margin-bottom:16px}
          table{width:100%;border-collapse:collapse}
          .promo{display:flex;align-items:center;gap:11px;margin-top:24px;padding:9px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;text-decoration:none;color:#0f172a}
          .promo .picon{border-radius:6px;flex:0 0 auto}
          .promo .pbody{display:flex;flex-direction:column;gap:2px}
          .promo .ptext{font-size:13px}
          .promo .purl{font-size:11px;font-weight:700;color:#2563eb;white-space:nowrap}
          .promo .pqr{margin-left:auto;width:56px;height:56px;border:1px solid #e2e8f0;background:#fff;flex:0 0 auto}
          .foot{margin-top:12px;color:#666;font-size:11px}
          @media print{body{margin:0}}</style></head>
          <body>
          <div class="head">
            <img class="logo" src="${origin}/calculatorscope-logo.svg" alt="Calculator Scope" />
            <div>
              <h1>${esc(h1)}</h1>
              <div class="meta">${esc(host)} &middot; ${esc(when)}</div>
              <div class="gen">Generated by CalculatorScope.com</div>
            </div>
          </div>
          <div class="verdict">${esc(t.ui.headline[r.tone])}</div>
          <table>${rows}</table>
          <a class="promo" href="${SUDOKUFLAGS_LINK}">
            <img class="picon" src="${origin}/sudokuflags-icon.png" alt="" width="30" height="30" />
            <span class="pbody">
              <span class="ptext"><b>${esc(SUDOKUFLAGS_BRAND)}:</b> ${esc(promo.text)}</span>
              <span class="purl">www.sudokuflags.com</span>
            </span>
            <img class="pqr" src="${origin}/sudokuflags-qr.svg" alt="QR www.sudokuflags.com" width="56" height="56" />
          </a>
          <script>window.onload=function(){window.print()}<\/script>
          </body></html>`
        const w = window.open('', '_blank')
        if (w) { w.document.open(); w.document.write(doc); w.document.close() }
    }

    return (
        <div className="w-full lg:w-[460px] lg:float-left lg:mr-5 mb-5 relative mx-auto lg:mx-0" style={{ maxWidth: '460px' }}>
            <div className="bg-blue-600 text-white px-5 py-3 rounded-t-lg">
                <h2 className="text-lg font-bold">{h1}</h2>
            </div>

            <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg p-5">
                <form onSubmit={run} className="flex gap-2">
                    <input
                        type="text"
                        inputMode="url"
                        autoCapitalize="none"
                        spellCheck={false}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder={t.ui.placeholder}
                        autoComplete="off"
                        className="flex-1 min-w-0 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap"
                    >
                        {loading ? t.ui.checking : t.ui.button}
                    </button>
                </form>

                {error && <p className="mt-4 text-sm text-red-600 font-medium">{error}</p>}

                {report && (
                    <div className="mt-4">
                        <div className={`flex items-center gap-3 p-3 rounded-lg text-white ${HEAD[report.tone]}`}>
                            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-white/25 font-bold">
                                {HEAD_ICON[report.tone]}
                            </span>
                            <div>
                                <div className="font-semibold">{t.ui.headline[report.tone]}</div>
                                {report.final_url && <div className="text-xs text-white/85 break-all">{report.final_url}</div>}
                            </div>
                        </div>

                        <ul className="mt-2">
                            {report.findings.map((f, i) => {
                                const ft = formatFinding(t, f.code, f.params)
                                return (
                                    <li key={i} className="flex gap-3 py-3 border-t border-gray-100 items-start">
                                        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-none ${DOT[f.status]}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900 text-sm">{ft.title}</p>
                                            <p className="text-gray-600 text-sm mt-0.5">{ft.detail}</p>
                                        </div>
                                        <span className={`flex-none text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mt-0.5 ${PILL[f.status]}`}>
                                            {t.ui.labels[f.status]}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>

                        <div className="flex justify-end mt-4">
                            <button
                                type="button"
                                onClick={() => downloadPdf(report)}
                                className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md text-gray-700 hover:border-blue-600 hover:text-blue-600"
                            >
                                {t.ui.downloadPdf}
                            </button>
                        </div>
                    </div>
                )}

                <p className="mt-4 text-xs text-gray-500 leading-relaxed">{t.ui.authNote}</p>

                <WidgetFooter
                    lang={lang}
                    toolSlug={toolSlug}
                    toolId={toolId}
                    h1={h1}
                    h1En={h1En}
                    translations={FOOTER[lang] || FOOTER.en}
                />
            </div>
        </div>
    )
}

function mapError(t: SiteCheckPack, status: number, code?: string): string {
    if (status === 429 || code === 'rate_limited') return t.ui.errRate
    if (code === 'missing_url') return t.ui.errMissing
    if (code === 'bad_scheme') return t.ui.errBadScheme
    if (code === 'blocked internal address') return t.ui.errBlocked
    if (code === 'bad_url') return t.ui.errBadUrl
    return t.ui.errNetwork
}
